// ============================================================================
// CHANGO V5.0 — Ops CMS · Pages index (Task #51 Phase 1)
// ============================================================================
// Lists every seeded marketing page, the editor-facing revision, the published
// revision, and its status. Admin picks a row → lands on the per-page editor.
//
// Backed by GET /api/ops/site/pages (routes/ops/siteCms.js).
// Admin-role gated upstream via <RoleGate allow={['admin']}> in main.jsx.
// ============================================================================

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { api, HttpError } from '../../../api/client.js';
import { useAuth } from '../../../hooks/useAuth.js';
import { formatDateTime } from './cmsHelpers.js';

function StatusBadge({ status }) {
  const cfg = {
    draft:     ['bg-slate-700  text-slate-200',   '草稿'],
    published: ['bg-emerald-700 text-emerald-100', '已发布'],
    archived:  ['bg-amber-700  text-amber-100',   '已归档'],
  }[status] || ['bg-slate-700 text-slate-200', status || '—'];
  return (
    <span className={`rounded px-2 py-0.5 text-xs font-medium ${cfg[0]}`}>
      {cfg[1]}
    </span>
  );
}

function usePages() {
  const [state, setState] = useState({ items: [], loading: true, error: null });
  const [reloadKey, setReloadKey] = useState(0);
  const reload = useCallback(() => setReloadKey((x) => x + 1), []);

  useEffect(() => {
    let cancelled = false;
    setState((s) => ({ ...s, loading: true, error: null }));
    (async () => {
      try {
        const res = await api.get('/api/ops/site/pages');
        if (cancelled) return;
        setState({
          items: Array.isArray(res?.items) ? res.items : [],
          loading: false,
          error: null,
        });
      } catch (err) {
        if (cancelled) return;
        if (err instanceof HttpError && err.status === 404) {
          setState({ items: [], loading: false, error: null });
        } else {
          setState((s) => ({ ...s, loading: false, error: err.message || '加载失败' }));
        }
      }
    })();
    return () => { cancelled = true; };
  }, [reloadKey]);

  return { ...state, reload };
}

export default function OpsCmsPages() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { items, loading, error, reload } = usePages();

  const sorted = useMemo(() => {
    // Keep the seed order (landing first, pricing next …) — it matches what the
    // planning doc documents and makes the console scan-friendly.
    const order = ['landing', 'pricing', 'product', 'matrix', 'cases',
                   'help', 'blog', 'about', 'contact'];
    return [...items].sort((a, b) => {
      const ai = order.indexOf(a.slug);
      const bi = order.indexOf(b.slug);
      if (ai !== -1 && bi !== -1) return ai - bi;
      if (ai !== -1) return -1;
      if (bi !== -1) return 1;
      return String(a.slug).localeCompare(String(b.slug));
    });
  }, [items]);

  return (
    <div className="min-h-screen bg-slate-900 text-slate-100">
      <header className="border-b border-slate-700 bg-slate-800/80">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-4">
          <div>
            <h1 className="text-lg font-semibold">CMS · 页面管理</h1>
            <p className="text-xs text-slate-400">
              {user?.email} · {user?.role} · 营销站 9 页 (§3 site-web)
              · <Link to="/ops/cms/notices" className="underline">站内公告</Link>
              · <Link to="/ops/dashboard" className="underline">返回 Ops</Link>
            </p>
          </div>
          <button
            type="button"
            onClick={reload}
            className="rounded border border-slate-600 px-3 py-1 text-xs text-slate-200 hover:bg-slate-700"
          >
            刷新
          </button>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-6 py-8">
        {error && (
          <div className="mb-4 rounded-md border border-rose-500/40 bg-rose-900/30 px-4 py-2 text-sm text-rose-200">
            {error}
          </div>
        )}

        <div className="overflow-hidden rounded-lg border border-slate-700 bg-slate-800/40">
          <table className="w-full text-sm">
            <thead className="bg-slate-800/80 text-left text-xs font-medium uppercase tracking-wide text-slate-400">
              <tr>
                <th className="px-4 py-3">Slug</th>
                <th className="px-4 py-3">路由</th>
                <th className="px-4 py-3">标题</th>
                <th className="px-4 py-3">状态</th>
                <th className="px-4 py-3 text-right">草稿</th>
                <th className="px-4 py-3 text-right">线上</th>
                <th className="px-4 py-3">最近修改</th>
                <th className="px-4 py-3 text-right">操作</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={8} className="px-4 py-10 text-center text-slate-400">加载中…</td></tr>
              )}
              {!loading && sorted.length === 0 && (
                <tr><td colSpan={8} className="px-4 py-10 text-center text-slate-400">尚无页面。请先运行 site-cms migration.</td></tr>
              )}
              {!loading && sorted.map((p) => (
                <tr key={p.slug} className="border-t border-slate-700/60 hover:bg-slate-700/20">
                  <td className="px-4 py-3 font-mono text-xs">{p.slug}</td>
                  <td className="px-4 py-3 font-mono text-xs text-slate-300">{p.route}</td>
                  <td className="px-4 py-3">{p.title}</td>
                  <td className="px-4 py-3"><StatusBadge status={p.status} /></td>
                  <td className="px-4 py-3 text-right tabular-nums">r{p.current_revision}</td>
                  <td className="px-4 py-3 text-right tabular-nums">
                    {p.published_revision > 0
                      ? <span className="text-emerald-300">r{p.published_revision}</span>
                      : <span className="text-slate-500">—</span>}
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-400">{formatDateTime(p.updated_at)}</td>
                  <td className="px-4 py-3 text-right">
                    <button
                      type="button"
                      onClick={() => navigate(`/ops/cms/pages/${encodeURIComponent(p.slug)}`)}
                      className="rounded bg-chango-600 px-3 py-1 text-xs font-semibold text-white hover:bg-chango-700"
                    >
                      编辑
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <p className="mt-6 text-center text-xs text-slate-500">
          Phase 1：仅提供 Hero / Pricing 块编辑 + 全站公告。Phase 2 将开放 features / cases / FAQ 等块种。
        </p>
      </main>
    </div>
  );
}
